/**
 * Regulatory Approvals Types
 * Defines all TypeScript interfaces for the regulatory approvals workflow
 */

// Enums
export enum ApprovalCategory {
  REGULATORY_AUTHORITY = "REGULATORY_AUTHORITY",
  IRB_EC = "IRB_EC",
}

export enum ApprovalType {
  INITIAL = "INITIAL",
  AMENDMENT = "AMENDMENT",
  RENEWAL = "RENEWAL",
}

export enum ApprovalStatus {
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  EXPIRED = "EXPIRED",
  RENEWAL_REQUIRED = "RENEWAL_REQUIRED",
}

export enum ExpiryAlertSeverity {
  CRITICAL = "CRITICAL",
  WARNING = "WARNING",
  INFO = "INFO",
}

// Main Types
export interface RegulatoryApproval {
  id: string;
  tenantId: string;
  studyId: string;
  studyName: string | null;
  siteId: string | null;
  siteName: string | null;
  category: ApprovalCategory;
  approvalType: ApprovalType;
  approvalNumber: string | null;
  issuingAuthority: string;
  countryRegion: string | null;
  validFrom: string;
  validUntil: string;
  submissionDate: string | null;
  status: ApprovalStatus;
  isActive: boolean;
  currentVersion: number;
  notes: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  // Optional computed/additional fields
  approvalDate?: string; // Alias for validFrom
  expiryDate?: string; // Alias for validUntil
  daysToExpiry?: number;
  documents?: DocumentVersion[];
  timeline?: TimelineEvent[];
}

export interface DocumentVersion {
  id: string;
  approvalId: string;
  tenantId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  version: number;
  versionNotes?: string;
  storagePath: string;
  uploadedBy: string;
  uploadedAt: string;
  storageProvider: string;
  isActive: boolean;
  downloadUrl?: string;
  s3Url?: string;
}

export interface TimelineEvent {
  id: string;
  type:
    | "CREATED"
    | "UPDATED"
    | "EXPIRED"
    | "RENEWAL"
    | "REMINDER"
    | "ARCHIVED"
    | "VERSION_UPLOADED";
  title: string;
  description: string;
  timestamp: string;
  user?: string;
}

export interface ExpiryAlert {
  id: string;
  approvalId: string;
  approvalNumber: string;
  studyName: string;
  siteName?: string;
  siteId?: string;
  approvalType: ApprovalType;
  category: ApprovalCategory;
  issuingAuthority: string;
  expiryDate: string;
  daysToExpiry: number;
  severity: ExpiryAlertSeverity;
  status: ApprovalStatus;
}

export interface ApprovalFilters {
  limit: number;
  page: number;
  study?: string;
  site?: string;
  status?: ApprovalStatus;
  category?: ApprovalCategory;
  approvalType?: ApprovalType;
  showArchived?: boolean;
  expiringWithinDays?: number;
  searchTerm?: string;
}

export interface ApprovalSummary {
  totalApprovals: number;
  activeApprovals: number;
  expiringWithin30Days: number;
  expiredApprovals: number;
  pendingRenewals: number;
  archivedApprovals: number;
}

export interface ActivationStatus {
  canActivate: boolean;
  status: "ALLOWED" | "PENDING" | "BLOCKED";
  message: string;
  blockedBy?: string[];
  requiredApprovals?: string[];
}

export interface UploadApprovalResponse {
  id: string;
  approvalId: string;
  fileName: string;
  version: number;
  uploadedAt: string;
  status: "UPLOADED" | "PROCESSING" | "COMPLETED";
}

export interface ApprovalsListResponse {
  items: RegulatoryApproval[];
  total: number;
  page: number;
  limit: number;
}

export interface RegulatoryAuthority {
  id: string;
  name: string;
  fullName: string;
  category: ApprovalCategory;
  country?: string;
  countryName?: string;
  abbreviation: string;
  description?: string;
  isActive: boolean;
}
